import { useCallback, useEffect, useRef, useState } from "react";
import type { FormEvent, KeyboardEvent } from "react";
import { SUPPORT_CHAT_LIMITS } from "@platform/shared";
import type { SupportChatMessage } from "@platform/shared";
import { SupportChatError, sendSupportChat } from "@/lib/support-chat-client";
import { SITE } from "@/lib/site-config";

/**
 * Floating website support chat. Public pages only (mounted on the landing
 * page) -- never inside /dashboard or /admin.
 *
 * Privacy: collects nothing but the question text. No name/email prompt, no
 * cookies, no localStorage; the conversation exists only in this component's
 * state and is gone when the page closes. It talks to the anonymous
 * /api/support-chat endpoint, which has no access to any customer data.
 */

const WELCOME: SupportChatMessage = {
  role: "assistant",
  content:
    "Hi! I can answer questions about Callback — what it does, its features, pricing, and how to get support. What would you like to know?",
};

const SUGGESTIONS = ["How does Callback work?", "What does it cost?", "Can I cancel anytime?"];

type Status = "idle" | "loading" | "error";

export function SupportChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<SupportChatMessage[]>([WELCOME]);
  const [input, setInput] = useState("");
  const [status, setStatus] = useState<Status>("idle");
  const [error, setError] = useState<string | null>(null);
  const [cooldownUntil, setCooldownUntil] = useState<number>(0);
  const [now, setNow] = useState(() => Date.now());

  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const launcherRef = useRef<HTMLButtonElement>(null);
  const inFlight = useRef(false);

  const loading = status === "loading";
  const coolingDown = cooldownUntil > now;

  // The welcome message is UI-only; only real turns are sent to the API.
  const request = useCallback(async (conversation: SupportChatMessage[]) => {
    if (inFlight.current) return;
    inFlight.current = true;
    setStatus("loading");
    setError(null);
    try {
      const reply = await sendSupportChat(conversation.slice(-SUPPORT_CHAT_LIMITS.maxMessages));
      setMessages((prev) => [...prev, { role: "assistant", content: reply }]);
      setStatus("idle");
    } catch (err) {
      const e = err instanceof SupportChatError ? err : null;
      setError(e?.message ?? "Something went wrong. Please try again.");
      if (e?.kind === "rate_limited") setCooldownUntil(Date.now() + (e.retryAfterSeconds ?? 30) * 1000);
      setStatus("error");
    } finally {
      inFlight.current = false;
    }
  }, []);

  const conversationForApi = (list: SupportChatMessage[]) => list.filter((m) => m !== WELCOME);

  const submit = useCallback(
    (text: string) => {
      const content = text.trim();
      if (!content || inFlight.current) return;
      const next = [...messages, { role: "user" as const, content: content.slice(0, SUPPORT_CHAT_LIMITS.maxUserMessageChars) }];
      setMessages(next);
      setInput("");
      void request(conversationForApi(next));
    },
    [messages, request]
  );

  function retry() {
    // The failed question is still the last message; just resend.
    void request(conversationForApi(messages));
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!coolingDown) submit(input);
  }

  function onKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (!coolingDown) submit(input);
    }
  }

  // Keep the newest message in view.
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [messages, status, error, open]);

  // Tick while rate-limited so the countdown / disabled state clears itself.
  useEffect(() => {
    if (!coolingDown) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [coolingDown]);

  // Focus management + Escape to close.
  useEffect(() => {
    if (!open) return;
    inputRef.current?.focus();
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
        launcherRef.current?.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  const secondsLeft = Math.max(0, Math.ceil((cooldownUntil - now) / 1000));
  const showSuggestions = messages.length === 1 && status === "idle";
  const canSend = input.trim().length > 0 && !loading && !coolingDown;

  return (
    <>
      {open && (
        <section
          role="dialog"
          aria-label="Callback support chat"
          className="fixed inset-x-0 bottom-0 z-50 flex h-[85dvh] max-h-[100dvh] flex-col overflow-hidden rounded-t-2xl border border-line bg-paper-raised shadow-2xl sm:inset-x-auto sm:bottom-24 sm:right-5 sm:h-[34rem] sm:w-[23rem] sm:rounded-2xl"
        >
          <header className="flex items-start justify-between gap-3 border-b border-line bg-brand-900 px-4 py-3 text-white">
            <div>
              <p className="font-display text-base leading-tight">Callback assistant</p>
              <p className="mt-0.5 text-xs text-brand-100">Automated — answers questions about Callback</p>
            </div>
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                launcherRef.current?.focus();
              }}
              aria-label="Close chat"
              className="-mr-1 rounded-md p-1.5 text-brand-100 hover:bg-white/10 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
            >
              <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                <path d="M6 6l12 12M18 6 6 18" />
              </svg>
            </button>
          </header>

          <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto px-4 py-4" aria-live="polite" aria-busy={loading}>
            {messages.map((m, i) => (
              <div key={i} className={m.role === "user" ? "flex justify-end" : "flex justify-start"}>
                <p
                  className={
                    m.role === "user"
                      ? "max-w-[85%] whitespace-pre-wrap break-words rounded-2xl rounded-br-sm bg-brand-600 px-3.5 py-2 text-sm text-white"
                      : "max-w-[85%] whitespace-pre-wrap break-words rounded-2xl rounded-bl-sm bg-paper px-3.5 py-2 text-sm text-ink ring-1 ring-line"
                  }
                >
                  {/* Rendered as plain text, never HTML. */}
                  {m.content}
                </p>
              </div>
            ))}

            {showSuggestions && (
              <div className="flex flex-wrap gap-2 pt-1">
                {SUGGESTIONS.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => submit(s)}
                    className="rounded-full border border-brand-300 bg-white px-3 py-1.5 text-xs text-brand-700 hover:bg-brand-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}

            {loading && (
              <div className="flex justify-start" role="status" aria-label="Assistant is typing">
                <div className="flex items-center gap-1 rounded-2xl rounded-bl-sm bg-paper px-4 py-3 ring-1 ring-line">
                  {[0, 150, 300].map((delay) => (
                    <span key={delay} className="h-1.5 w-1.5 animate-bounce rounded-full bg-ink-soft" style={{ animationDelay: `${delay}ms` }} />
                  ))}
                </div>
              </div>
            )}

            {status === "error" && error && (
              <div role="alert" className="rounded-lg border border-signal-300 bg-signal-50 px-3 py-2.5 text-sm text-ink">
                <p>{error}</p>
                <div className="mt-2 flex flex-wrap items-center gap-3 text-xs">
                  <button
                    type="button"
                    onClick={retry}
                    disabled={coolingDown}
                    className="rounded-md bg-signal-500 px-2.5 py-1 font-medium text-white hover:bg-signal-600 disabled:cursor-not-allowed disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-signal-600"
                  >
                    {coolingDown ? `Try again in ${secondsLeft}s` : "Try again"}
                  </button>
                  <a href={`mailto:${SITE.demoContactEmail}`} className="text-brand-700 underline">
                    Email the team
                  </a>
                </div>
              </div>
            )}
          </div>

          <form onSubmit={onSubmit} className="border-t border-line bg-paper-raised p-3">
            <div className="flex items-end gap-2">
              <label htmlFor="support-chat-input" className="sr-only">
                Your question
              </label>
              <textarea
                id="support-chat-input"
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKeyDown}
                maxLength={SUPPORT_CHAT_LIMITS.maxUserMessageChars}
                rows={1}
                placeholder="Ask about Callback…"
                // 16px font on mobile avoids iOS Safari zooming the page on focus.
                className="max-h-28 min-h-[2.5rem] flex-1 resize-none rounded-lg border border-line bg-white px-3 py-2 text-base text-ink placeholder:text-ink-soft focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 sm:text-sm"
              />
              <button
                type="submit"
                disabled={!canSend}
                className="h-10 rounded-lg bg-brand-600 px-4 text-sm font-medium text-white hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2"
              >
                Send
              </button>
            </div>
            <p className="mt-2 text-[11px] leading-snug text-ink-soft">
              Automated assistant — it can't see any account. Please don't share personal or payment details. This chat isn't saved and
              clears when you leave the page.
            </p>
          </form>
        </section>
      )}

      <button
        ref={launcherRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-label={open ? "Close support chat" : "Open support chat"}
        className={`fixed bottom-4 right-4 z-40 flex h-14 w-14 items-center justify-center rounded-full bg-brand-600 text-white shadow-lg hover:bg-brand-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 sm:bottom-5 sm:right-5 ${open ? "hidden sm:flex" : ""}`}
      >
        <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          {open ? <path d="M6 6l12 12M18 6 6 18" /> : <path d="M4 5h16v11H8l-4 4V5Z" />}
        </svg>
      </button>
    </>
  );
}
